import type {
  Form,
  FormAccessMode,
  FormField,
  FormFieldInput,
  FormFieldType,
  FormStatus,
  FormToken,
} from '@pm/schemas';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Counter,
  ErrorState,
  Field,
  Input,
  Kbd,
  LabelCaps,
  PageHeader,
  Select,
  Skeleton,
  Tabs,
  Textarea,
} from '@pm/ui';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Copy,
  ExternalLink,
  KeyRound,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { formKeys, useClients, useForm, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import {
  ACCESS_MODE_HINT,
  ACCESS_MODE_LABEL,
  AccessModeBadge,
  copyText,
  FIELD_TYPE_LABEL,
  publicFormUrl,
} from './form-bits';

type Draft = FormFieldInput & { key: string };

const toDraft = (f: FormField): Draft => ({
  key: f.id,
  id: f.id,
  field_type: f.field_type,
  label: f.label,
  name: f.name,
  required: f.required,
  options: f.options,
  placeholder: f.placeholder,
  help_text: f.help_text,
});
const newDraft = (type: FormFieldType): Draft => ({
  key: crypto.randomUUID(),
  field_type: type,
  label: '',
  required: false,
  options: type === 'SELECT' || type === 'MULTISELECT' ? ['Opção 1'] : [],
  placeholder: null,
  help_text: null,
});

/** Editor do formulário: configurações, token e construtor de campos. Prioridade nunca é um campo (RULE-014). */
export function FormEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { data: form, isLoading, isError, error, refetch } = useForm(id);
  const { can } = useAuth();
  const [tab, setTab] = useState<'fields' | 'settings'>('fields');
  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !form)
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  const canManage = can('forms.manage');
  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
            <Link to="/formularios" className="hover:text-text-1">
              Formulários
            </Link>
            <ChevronRight className="size-3" />
            <span>{form.slug}</span>
          </div>
        }
        title={form.name}
        subtitle={form.description ?? undefined}
        actions={
          <>
            <AccessModeBadge mode={form.access_mode} />
            {form.status !== 'ACTIVE' ? (
              <Badge tone="neutral">{form.status === 'INACTIVE' ? 'Inativo' : 'Arquivado'}</Badge>
            ) : null}
            <Link
              to={`/triagem?form=${form.id}`}
              className="inline-flex h-control items-center gap-1.5 rounded border border-border px-3 text-sm hover:bg-surface-2"
            >
              Solicitações{' '}
              {form.pending_count > 0 ? (
                <Counter tone="accent">{form.pending_count}</Counter>
              ) : (
                <span className="font-mono text-xs text-text-3">{form.requests_count}</span>
              )}
            </Link>
          </>
        }
      />
      <ShareCard form={form} canManage={canManage} />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'fields', label: 'Campos', count: form.fields.length },
          { value: 'settings', label: 'Configurações' },
        ]}
      />
      {tab === 'fields' ? (
        <FieldsBuilder form={form} canManage={canManage} />
      ) : (
        <SettingsForm form={form} canManage={canManage} />
      )}
    </div>
  );
}

function ShareCard({ form, canManage }: { form: Form; canManage: boolean }) {
  const toast = useToast();
  const [token, setToken] = useState<FormToken | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const rotate = useInvalidating(
    () => api.post<FormToken>(`/forms/${form.id}/token`),
    [formKeys.form(form.id), formKeys.forms],
  );
  const url = publicFormUrl(form.slug);
  const shareUrl = token ? `${url}?token=${token.token}` : url;
  const copy = (text: string) =>
    void copyText(text).then((ok) =>
      ok ? toast.success('Copiado') : toast.error('Não foi possível copiar'),
    );

  if (form.access_mode === 'AUTHENTICATED_CLIENT') {
    return (
      <div className="rounded-md border border-border bg-surface-1 px-4 py-3 text-sm text-text-2">
        Modo autenticado: não há link público. Usuários internos podem registrar solicitações em
        nome de um contato pela API (
        <code className="font-mono text-xs">POST /client/forms/{form.id}/submit</code>); o portal do
        cliente usará este modo.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <LabelCaps>Link do formulário</LabelCaps>
        <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 font-mono text-xs">
          {shareUrl}
        </code>
        <Button variant="secondary" size="sm" onClick={() => copy(shareUrl)}>
          <Copy /> Copiar
        </Button>
        {form.access_mode === 'PUBLIC_URL' || token ? (
          <a
            className="inline-flex h-control-sm items-center gap-1.5 rounded border border-border px-2.5 text-sm hover:bg-surface-2"
            href={shareUrl}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink className="size-3.5" /> Abrir
          </a>
        ) : null}
      </div>
      {form.access_mode === 'TOKEN_PROTECTED' ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <KeyRound className="size-4 text-text-3" />
          {token ? (
            <span className="text-warning">
              Token gerado. Copie o link acima agora: ele não será mostrado de novo.
            </span>
          ) : form.has_token ? (
            <span className="text-text-2">
              Token ativo. O link só funciona com{' '}
              <span className="font-mono text-xs">?token=…</span>; gere outro para invalidar o
              atual.
            </span>
          ) : (
            <span className="text-text-2">
              Nenhum token gerado ainda; o link não abre até você gerar um.
            </span>
          )}
          {canManage ? (
            <Button
              variant={form.has_token ? 'ghost' : 'primary'}
              size="sm"
              className="ml-auto"
              loading={rotate.isPending}
              onClick={() =>
                form.has_token
                  ? setConfirmRotate(true)
                  : rotate.mutate(undefined, {
                      onSuccess: setToken,
                      onError: (e) => toast.error(e),
                    })
              }
            >
              {form.has_token ? 'Gerar novo token' : 'Gerar token'}
            </Button>
          ) : null}
          <ConfirmDialog
            open={confirmRotate}
            onOpenChange={setConfirmRotate}
            title="Gerar novo token?"
            description="O link atual deixa de funcionar imediatamente. Você precisará enviar o novo link ao cliente."
            confirmLabel="Gerar"
            danger
            loading={rotate.isPending}
            onConfirm={() =>
              rotate.mutate(undefined, {
                onSuccess: (t) => (setToken(t), setConfirmRotate(false)),
                onError: (e) => toast.error(e),
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function SettingsForm({ form, canManage }: { form: Form; canManage: boolean }) {
  const toast = useToast();
  const me = useMe();
  const agency = me.organization?.mode === 'AGENCY';
  const { data: clients } = useClients({ status: 'ACTIVE' });
  const [name, setName] = useState(form.name);
  const [description, setDescription] = useState(form.description ?? '');
  const [mode, setMode] = useState<FormAccessMode>(form.access_mode);
  const [status, setStatus] = useState<FormStatus>(form.status);
  const [clientId, setClientId] = useState(form.client?.id ?? '');
  const [success, setSuccess] = useState(form.success_message ?? '');
  const [remove, setRemove] = useState(false);
  const save = useInvalidating(
    (input: Record<string, unknown>) => api.patch<Form>(`/forms/${form.id}`, input),
    [formKeys.form(form.id), formKeys.forms],
  );
  const del = useInvalidating(() => api.delete(`/forms/${form.id}`), [formKeys.forms]);
  return (
    <form
      className="flex max-w-[640px] flex-col gap-4 rounded-md border border-border bg-surface-1 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            name,
            description: description || null,
            access_mode: mode,
            status,
            client_id: clientId || null,
            success_message: success || null,
          },
          {
            onSuccess: () => toast.success('Configurações salvas'),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Nome" htmlFor="s-name">
        <Input
          id="s-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canManage}
        />
      </Field>
      <Field label="Descrição (aparece para o cliente)" htmlFor="s-desc">
        <Textarea
          id="s-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={!canManage}
          className="min-h-[64px]"
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Acesso" htmlFor="s-mode" hint={ACCESS_MODE_HINT[mode]}>
          <Select
            id="s-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as FormAccessMode)}
            disabled={!canManage}
          >
            {(Object.keys(ACCESS_MODE_LABEL) as FormAccessMode[]).map((m) => (
              <option key={m} value={m}>
                {ACCESS_MODE_LABEL[m]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Status" htmlFor="s-status" hint="Inativo: o link para de aceitar envios.">
          <Select
            id="s-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as FormStatus)}
            disabled={!canManage}
          >
            <option value="ACTIVE">Ativo</option>
            <option value="INACTIVE">Inativo</option>
          </Select>
        </Field>
      </div>
      {agency ? (
        <Field
          label="Cliente"
          htmlFor="s-client"
          hint="Solicitações herdam este cliente; contatos novos são criados nele ao aprovar."
        >
          <Select
            id="s-client"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={!canManage}
          >
            <option value="">Sem cliente fixo (definir na triagem)</option>
            {(clients?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.trade_name ?? c.legal_name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field label="Mensagem após o envio" htmlFor="s-success">
        <Textarea
          id="s-success"
          value={success}
          onChange={(e) => setSuccess(e.target.value)}
          disabled={!canManage}
          placeholder="Recebemos sua solicitação. Nossa equipe fará a triagem em breve."
          className="min-h-[64px]"
        />
      </Field>
      {canManage ? (
        <div className="flex items-center gap-2">
          <Button variant="primary" type="submit" loading={save.isPending}>
            <Save /> Salvar
          </Button>
          <Button
            variant="ghost"
            type="button"
            className="ml-auto text-danger"
            onClick={() => setRemove(true)}
          >
            <Trash2 /> Arquivar formulário
          </Button>
        </div>
      ) : null}
      <ConfirmDialog
        open={remove}
        onOpenChange={setRemove}
        title={`Arquivar ${form.name}?`}
        description="O link deixa de funcionar. As solicitações já recebidas continuam na Triagem."
        confirmLabel="Arquivar"
        danger
        loading={del.isPending}
        onConfirm={() =>
          del.mutate(undefined, {
            onSuccess: () => (toast.success('Formulário arquivado'), window.history.back()),
            onError: (e) => toast.error(e),
          })
        }
      />
    </form>
  );
}

function FieldsBuilder({ form, canManage }: { form: Form; canManage: boolean }) {
  const toast = useToast();
  const [drafts, setDrafts] = useState<Draft[]>(() => form.fields.map(toDraft));
  const [dirty, setDirty] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const save = useInvalidating(
    (fields: FormFieldInput[]) => api.put<Form>(`/forms/${form.id}/fields`, { fields }),
    [formKeys.form(form.id), formKeys.forms],
  );

  const update = (key: string, patch: Partial<Draft>) => {
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
    setDirty(true);
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= drafts.length) return;
    const next = [...drafts];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setDrafts(next);
    setDirty(true);
  };
  const remove = (key: string) => {
    setDrafts((ds) => ds.filter((d) => d.key !== key));
    setDirty(true);
  };
  const add = (type: FormFieldType) => {
    setDrafts((ds) => [...ds, newDraft(type)]);
    setDirty(true);
    setAddOpen(false);
  };
  const valid = drafts.every(
    (d) =>
      d.label.trim().length > 0 &&
      ((d.field_type !== 'SELECT' && d.field_type !== 'MULTISELECT') ||
        d.options.some((o) => o.trim())),
  );

  return (
    <div className="grid gap-gap-lg lg:grid-cols-[1fr_300px]">
      <div className="flex flex-col gap-gap">
        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-3">
          <LabelCaps>Dados do solicitante (sempre presentes)</LabelCaps>
          <div className="flex flex-wrap gap-2 text-sm text-text-2">
            <Badge mono={false} tone="neutral">
              Nome *
            </Badge>
            <Badge mono={false} tone="neutral">
              E-mail *
            </Badge>
            <Badge mono={false} tone="neutral">
              Telefone
            </Badge>
            <span className="text-xs text-text-3">
              Usados para identificar o contato do cliente.
            </span>
          </div>
        </div>
        {drafts.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-surface-1 p-6 text-center text-sm text-text-2">
            Nenhum campo. Adicione ao lado.
          </div>
        ) : null}
        {drafts.map((d, i) => (
          <section
            key={d.key}
            className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-3"
          >
            <header className="flex items-center gap-2">
              <span className="font-mono text-xs text-text-3">{i + 1}</span>
              <Badge tone="accent" mono={false}>
                {FIELD_TYPE_LABEL[d.field_type]}
              </Badge>
              {d.name ? <span className="font-mono text-xs text-text-3">{d.name}</span> : null}
              {canManage ? (
                <span className="ml-auto flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Subir"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Descer"
                    disabled={i === drafts.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remover"
                    onClick={() => remove(d.key)}
                  >
                    <Trash2 />
                  </Button>
                </span>
              ) : null}
            </header>
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr]">
              <Field label="Rótulo" htmlFor={`fl-${d.key}`}>
                <Input
                  id={`fl-${d.key}`}
                  value={d.label}
                  onChange={(e) => update(d.key, { label: e.target.value })}
                  disabled={!canManage}
                  placeholder="Ex.: Objetivo da campanha"
                  className="h-control-sm text-sm"
                />
              </Field>
              <Field label="Placeholder" htmlFor={`fp-${d.key}`}>
                <Input
                  id={`fp-${d.key}`}
                  value={d.placeholder ?? ''}
                  onChange={(e) => update(d.key, { placeholder: e.target.value || null })}
                  disabled={!canManage}
                  className="h-control-sm text-sm"
                />
              </Field>
            </div>
            <Field label="Texto de ajuda" htmlFor={`fh-${d.key}`}>
              <Input
                id={`fh-${d.key}`}
                value={d.help_text ?? ''}
                onChange={(e) => update(d.key, { help_text: e.target.value || null })}
                disabled={!canManage}
                className="h-control-sm text-sm"
              />
            </Field>
            {d.field_type === 'SELECT' || d.field_type === 'MULTISELECT' ? (
              <Field label="Opções (uma por linha)" htmlFor={`fo-${d.key}`}>
                <Textarea
                  id={`fo-${d.key}`}
                  value={d.options.join('\n')}
                  onChange={(e) => update(d.key, { options: e.target.value.split('\n') })}
                  onBlur={() =>
                    update(d.key, { options: d.options.map((o) => o.trim()).filter(Boolean) })
                  }
                  disabled={!canManage}
                  className="min-h-[72px] font-mono text-xs"
                />
              </Field>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={d.required}
                onCheckedChange={(v) => update(d.key, { required: v === true })}
                disabled={!canManage}
              />{' '}
              Obrigatório
            </label>
          </section>
        ))}
      </div>
      <aside className="flex flex-col gap-3 self-start lg:sticky lg:top-4">
        {canManage ? (
          <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-3">
            <LabelCaps>Adicionar campo</LabelCaps>
            <div className={cn('grid grid-cols-2 gap-1.5', !addOpen && 'hidden lg:grid')}>
              {(Object.keys(FIELD_TYPE_LABEL) as FormFieldType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => add(t)}
                  className="h-8 rounded border border-border px-2 text-left text-sm text-text-2 transition-colors hover:border-accent-border hover:text-text-1"
                >
                  {FIELD_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="lg:hidden"
              onClick={() => setAddOpen((v) => !v)}
            >
              <Plus /> Campo
            </Button>
          </div>
        ) : null}
        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-3 text-sm text-text-2">
          <LabelCaps>Regras</LabelCaps>
          <p>Prioridade nunca aparece para o cliente: ela é definida na Triagem (RULE-014).</p>
          <p>O nome técnico do campo é gerado do rótulo e mantido ao renomear.</p>
        </div>
        {canManage ? (
          <Button
            variant="primary"
            disabled={!dirty || !valid}
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                drafts.map(({ key: _k, ...rest }) => ({
                  ...rest,
                  options: rest.options.map((o) => o.trim()).filter(Boolean),
                })),
                {
                  onSuccess: (f) => (
                    toast.success('Campos salvos'),
                    setDrafts(f.fields.map(toDraft)),
                    setDirty(false)
                  ),
                  onError: (e) => toast.error(e),
                },
              )
            }
          >
            <Save /> Salvar campos {dirty ? <Kbd>•</Kbd> : null}
          </Button>
        ) : null}
      </aside>
    </div>
  );
}
